import moment from "moment-timezone";

export const DAY_TIMELINE_START_HOUR = 6;
export const DAY_TIMELINE_END_HOUR = 23;
export const PX_PER_MINUTE = 2;
export const MIN_EVENT_HEIGHT = 36;
export const EVENT_TIER_MICRO_MAX = 32;
export const EVENT_TIER_COMPACT_MAX = 56;
export const TIME_GUTTER_WIDTH = 52;
export const EVENT_H_GAP = 4;

function getStartMinutes(dateValue, timeZone) {
  const m = moment(dateValue).tz(timeZone);
  return m.hours() * 60 + m.minutes();
}

function getEndMinutes(dateValue, endDateValue, timeZone) {
  const end = endDateValue ? moment(endDateValue).tz(timeZone) : moment(dateValue).tz(timeZone);
  return end.hours() * 60 + end.minutes();
}

export function getEventTier(height) {
  if (height < EVENT_TIER_MICRO_MAX) {
    return "micro";
  }
  if (height < EVENT_TIER_COMPACT_MAX) {
    return "compact";
  }
  return "full";
}

function packClusterColumns(cluster) {
  const columns = [];

  cluster.forEach((item) => {
    let placed = false;
    for (let col = 0; col < columns.length; col += 1) {
      if (columns[col] <= item.startMin) {
        item.column = col;
        columns[col] = item.endMin;
        placed = true;
        break;
      }
    }
    if (!placed) {
      item.column = columns.length;
      columns.push(item.endMin);
    }
  });

  const totalColumns = Math.max(columns.length, 1);
  cluster.forEach((item) => {
    item.totalColumns = totalColumns;
  });
}

function packOverlappingColumns(items) {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || b.height - a.height);
  const clusters = [];
  let currentCluster = [];
  let clusterEnd = -1;

  sorted.forEach((item) => {
    if (currentCluster.length === 0 || item.startMin < clusterEnd) {
      currentCluster.push(item);
      clusterEnd = Math.max(clusterEnd, item.endMin);
      return;
    }
    packClusterColumns(currentCluster);
    clusters.push(currentCluster);
    currentCluster = [item];
    clusterEnd = item.endMin;
  });

  if (currentCluster.length > 0) {
    packClusterColumns(currentCluster);
    clusters.push(currentCluster);
  }

  return sorted;
}

export function layoutDayEvents(events, timeZone, options = {}) {
  const dayStartHour = options.dayStartHour ?? DAY_TIMELINE_START_HOUR;
  const dayEndHour = options.dayEndHour ?? DAY_TIMELINE_END_HOUR;
  const pxPerMinute = options.pxPerMinute ?? PX_PER_MINUTE;
  const minEventHeight = options.minEventHeight ?? MIN_EVENT_HEIGHT;
  const dayStartMinutes = dayStartHour * 60;

  const items = (events || []).map((event) => {
    const startMin = getStartMinutes(event.dateDate, timeZone);
    let endMin = getEndMinutes(event.dateDate, event.endDateDate, timeZone);
    if (endMin <= startMin) {
      endMin = startMin + 15;
    }

    const duration = endMin - startMin;
    const top = (startMin - dayStartMinutes) * pxPerMinute;
    const timeHeight = duration * pxPerMinute;
    const height = Math.max(timeHeight, minEventHeight);

    return {
      event,
      top: Math.max(0, top),
      height,
      startMin,
      endMin,
      column: 0,
      totalColumns: 1,
      tier: getEventTier(height),
    };
  });

  const packed = packOverlappingColumns(items);

  return {
    items: packed,
    timelineHeight: (dayEndHour - dayStartHour) * 60 * pxPerMinute,
    dayStartHour,
    dayEndHour,
    pxPerMinute,
  };
}

export function getTimelineHourLabels(dayStartHour, dayEndHour) {
  const labels = [];
  for (let hour = dayStartHour; hour <= dayEndHour; hour += 1) {
    labels.push(hour);
  }
  return labels;
}
